import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AdminUser {
  id: string;
  email: string;
  role: 'agency_admin' | 'super_admin' | 'supervisor';
  agency_id?: string;
  agency?: {
    id: string;
    name: string;
    code: string;
  };
  is_active: boolean;
}

interface SignInResult {
  error?: string;
  requires2FA?: boolean;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  verifyOtp: (email: string, password: string, otp: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  refreshUser: (sessionOverride?: Session | null) => Promise<AdminUser | null>;
}

const buildAdminUserFromProfile = (profile: any): AdminUser | null => {
  if (!profile) return null;
  if (profile.is_active !== true) return null;

  const role = profile.role as AdminUser['role'] | undefined;
  if (role !== 'agency_admin' && role !== 'super_admin' && role !== 'supervisor') return null;

  const agency = profile.agencies
    ? {
        id: profile.agencies.id,
        name: profile.agencies.name,
        code: profile.agencies.code,
      }
    : undefined;

  return {
    id: profile.user_id,
    email: profile.email,
    role,
    agency_id: profile.agency_id ?? undefined,
    agency,
    is_active: profile.is_active,
  };
};

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider = ({ children }: AdminAuthProviderProps) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const refreshUser = async (sessionOverride?: Session | null): Promise<AdminUser | null> => {
    try {
      const session =
        sessionOverride ?? (await supabase.auth.getSession()).data.session;

      if (!session?.user) {
        setUser(null);
        return null;
      }

      // Fetch user profile with agency data and user_roles
      const [profileResult, rolesResult] = await Promise.all([
        supabase
          .from('profiles')
          .select(`
            user_id,
            email,
            role,
            agency_id,
            is_active,
            agencies (
              id,
              name,
              code
            )
          `)
          .eq('user_id', session.user.id)
          .eq('is_active', true)
          .single(),
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
      ]);

      const { data: profile, error } = profileResult;
      const { data: userRoles } = rolesResult;

      if (error || !profile) {
        setUser(null);
        return null;
      }

      // Check user_roles table for actual role (super_admin, agency_admin, supervisor)
      const roles = userRoles?.map(r => r.role) || [];
      let effectiveRole: AdminUser['role'] | null = null;
      
      if (roles.includes('super_admin')) {
        effectiveRole = 'super_admin';
      } else if (roles.includes('agency_admin')) {
        effectiveRole = 'agency_admin';
      } else if (roles.includes('supervisor')) {
        effectiveRole = 'supervisor';
      }

      // Fallback to profile.role if no user_roles found (for backward compatibility)
      if (!effectiveRole) {
        const profileRole = profile.role as AdminUser['role'] | undefined;
        if (profileRole === 'agency_admin' || profileRole === 'super_admin') {
          effectiveRole = profileRole;
        }
      }

      if (!effectiveRole) {
        setUser(null);
        return null;
      }

      const agency = profile.agencies
        ? {
            id: (profile.agencies as any).id,
            name: (profile.agencies as any).name,
            code: (profile.agencies as any).code,
          }
        : undefined;

      const adminUser: AdminUser = {
        id: profile.user_id,
        email: profile.email,
        role: effectiveRole,
        agency_id: profile.agency_id ?? undefined,
        agency,
        is_active: profile.is_active,
      };

      setUser(adminUser);
      return adminUser;
    } catch (error) {
      console.error('Error refreshing admin user:', error);
      setUser(null);
      return null;
    }
  };

  const signIn = async (email: string, password: string): Promise<SignInResult> => {
    try {
      setLoading(true);

      // Use admin-login edge function for secure authentication
      const { data, error } = await supabase.functions.invoke('admin-login', {
        body: { email, password }
      });

      if (error) {
        console.error('Admin login function error:', error);
        return { error: error.message || 'Login failed' };
      }

      if (data?.error) {
        return { error: data.error };
      }

      // If 2FA required, return to show OTP input
      if (data?.requires_2fa) {
        return { requires2FA: true };
      }

      // Login successful - set the session from the response
      if (data?.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      // Build user from returned profile or refresh
      const adminUserFromResponse = buildAdminUserFromProfile(data?.profile);

      // IMPORTANT: ensure we actually update context state before navigating to protected routes
      if (adminUserFromResponse) {
        setUser(adminUserFromResponse);
        return {};
      }

      const finalUser = await refreshUser();

      if (!finalUser) {
        return { error: 'Authentication failed. Please try again.' };
      }

      return {};
    } catch (error: any) {
      console.error('Sign in error:', error);
      return { error: error.message || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (email: string, password: string, otp: string): Promise<{ error?: string }> => {
    try {
      setLoading(true);

      // Call admin-login with OTP
      const { data, error } = await supabase.functions.invoke('admin-login', {
        body: { email, password, otp }
      });

      if (error) {
        console.error('OTP verification function error:', error);
        return { error: error.message || 'Verification failed' };
      }

      if (data?.error) {
        return { error: data.error };
      }

      // Set the session from the response
      if (data?.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      // Prefer the server-returned profile to avoid any RLS issues / timing races
      const adminUserFromResponse = buildAdminUserFromProfile(data?.profile);

      // IMPORTANT: ensure we actually update context state before navigating to protected routes
      if (adminUserFromResponse) {
        setUser(adminUserFromResponse);
        return {};
      }

      const finalUser = await refreshUser();

      if (!finalUser) {
        return { error: 'Authentication failed. Please try again.' };
      }

      return {};
    } catch (error: any) {
      console.error('OTP verification error:', error);
      return { error: error.message || 'Verification failed' };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      if (user) {
        // Log admin logout (do not block logout if logging fails)
        await supabase.from('admin_audit_logs').insert({
          actor_user_id: user.id,
          action: 'admin_logout',
          ip_address: '0.0.0.0',
        });
      }

      // Try global sign out first; if that fails (e.g. session_not_found), clear locally.
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn('Global sign out failed, clearing local session:', error.message);
        await supabase.auth.signOut({ scope: 'local' });
      }

      setUser(null);

      toast({
        title: 'Signed out successfully',
        description: 'You have been logged out of the admin panel.',
      });
    } catch (error) {
      console.error('Error signing out:', error);
      // Always clear local state even if something unexpected happens
      setUser(null);
      await supabase.auth.signOut({ scope: 'local' });
    }
  };

  useEffect(() => {
    // Listen for auth changes (do NOT call Supabase inside the callback; defer work)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setLoading(false);
        setTimeout(() => {
          refreshUser(session);
        }, 0);
      }
    });

    // Check initial session AFTER setting up the listener
    refreshUser().finally(() => setLoading(false));

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    signIn,
    verifyOtp,
    signOut,
    refreshUser,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
